import {useCallback, useEffect, useId, useRef, useState} from "react";

import {aiAssistantMessages} from "../ai-assistant.messages";

import {aiStore} from "./ai-store";
import {
  assistantMobileUIStore,
  focusAppOnMobile,
  focusAssistantOnMobile,
  isAssistantVisibleOnMobile,
} from "./mobile-assistant-ui.store";

function debugM4A(message: string, payload?: Record<string, unknown>) {
  // eslint-disable-next-line no-console
  console.debug(`[DEBUG-M4A] ${message}`, payload ?? {});
}

/**
 * Custom hook to manage the state and lifecycle of Human-in-the-Loop (HITL)
 * mutations triggered by an AI agent.
 *
 * It manages the `isOpen` state for the modal, the `draftData` drafted by the agent,
 * and crucially handles capturing the agent's `respond` callback, guaranteeing it
 * is executed even if the user closes the modal or navigates away.
 */
export function useHITLMutation<TDraft>() {
  const [isOpen, setIsOpen] = useState(false);
  const [draftData, setDraftData] = useState<Partial<TDraft> | undefined>();
  const respondRef = useRef<(result: unknown) => void>(undefined);
  const generatedId = useId();
  const blockerIdRef = useRef(`modal-hitl-${generatedId}`);

  const unregisterBlocker = useCallback(() => {
    debugM4A("modal blocker unregister", {blockerId: blockerIdRef.current});
    aiStore.state.unregisterConversationBlocker(blockerIdRef.current);
  }, []);

  // Unmount cleanup: if the page unmounts while the agent is waiting, abort the tool call.
  useEffect(() => {
    return () => {
      unregisterBlocker();

      if (respondRef.current) {
        respondRef.current("Action cancelled: user navigated away.");
        respondRef.current = undefined;
      }
    };
  }, [unregisterBlocker]);

  const initiate = (draft: Partial<TDraft> | undefined, respond: (result: unknown) => void) => {
    const shouldFocusApp = isAssistantVisibleOnMobile();

    if (shouldFocusApp) {
      assistantMobileUIStore.state.setPendingReturnToAssistant(true);
    }

    setDraftData(draft);
    setIsOpen(true);
    respondRef.current = respond;
    debugM4A("modal blocker register", {blockerId: blockerIdRef.current});
    aiStore.state.registerConversationBlocker({
      id: blockerIdRef.current,
      kind: "modal-hitl",
      label: aiAssistantMessages.actionRequired.defaultMessage,
    });

    if (shouldFocusApp) {
      window.requestAnimationFrame(() => {
        focusAppOnMobile();
      });
    }
  };

  const close = (result?: unknown) => {
    setIsOpen(false);
    debugM4A("modal blocker close", {blockerId: blockerIdRef.current});
    unregisterBlocker();

    if (respondRef.current) {
      // HeroUI's `onOpenChange(false)` passes a boolean `false` as result.
      if (result === false || result === undefined) {
        respondRef.current("Modal closed by user");
      } else {
        respondRef.current(result);
      }
      respondRef.current = undefined;
    }

    if (assistantMobileUIStore.state.pendingReturnToAssistant) {
      assistantMobileUIStore.state.clearPendingReturnToAssistant();
      focusAssistantOnMobile();
    }
  };

  return {
    isOpen,
    setIsOpen,
    setDraftData,
    draftData,
    initiate,
    close,
  };
}

export function isHITLSuccess(result: unknown): boolean {
  if (result === undefined || result === null) return false;
  try {
    const parsed = typeof result === "string" ? JSON.parse(result) : result;

    return typeof parsed === "object" && parsed !== null && parsed.success === true;
  } catch {
    return false;
  }
}
